'use strict';
import get from 'lodash/get';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/observable/combineLatest';
import 'rxjs/add/observable/empty';
import 'rxjs/add/observable/merge';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/throw';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/expand';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/pluck';
import 'rxjs/add/operator/reduce';
import 'rxjs/add/operator/startWith';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/take';
import 'rxjs/add/operator/takeUntil';
import 'rxjs/add/operator/throttleTime';
import {ViewMode, viewModeFromErrorStatus} from '../enums';
import {epicErrorHandler, expoBackoff, isRecoverable,} from '../utils';
import {getEtag,} from './utils';


export class ReduxItemEpics {
  /**
   * @param {ReduxObservableItemActions} actions
   * @param {ReduxObservableItemOptions} options
   * @param {ReduxObservableItemReducers} reducers
   */
  constructor(actions, options, reducers) {
    /** @type {ReduxObservableItemActions} **/
    this.actions = actions;
    /** @type {Logger} **/
    this.logger = options.logger;
    /** @type {ReduxObservableItemOptions} **/
    this._options = options;
    /** @type {ReduxObservableItemReducers} **/
    this._reducers = reducers;

    this.onDestroyItem = this.onDestroyItem.bind(this);
    this.onInitItem = this.onInitItem.bind(this);
    this.onSyncItem = this.onSyncItem.bind(this);
    this.requestItem = this.requestItem.bind(this);
    this.getCacheState = this.getCacheState.bind(this);

    this.epics = [
      this.onInitItem,
      this.onSyncItem,
    ];

    if (options.cacheOptions) {
      this.onItemUpdate = this.onItemUpdate.bind(this);
      this.addEpic(this.onItemUpdate);
    }

    if (options.useDelete) {
      this.onDeleteItem = this.onDeleteItem.bind(this);
      this.addEpic(this.onDeleteItem);
      this.requestDeleteItem = this.requestDeleteItem.bind(this);
    }
  }

  /**
   * Add a custom epic to the list.
   * @param epic
   */
  addEpic(epic) {
    this.epics.push(epic);
  }

  /**
   * Add custom epics to the list.
   * @param {Array<>} epics
   */
  addEpics(epics) {
    this.epics.push(...epics);
  }

  onDestroyItem(action$, initAction) {
    return action$.ofType(this.actions.destroyItem.TYPE)
      .filter(destroyAction => initAction.payload.id === get(destroyAction, 'meta.id'));
  }

  /**
   * Hydrate a cached item state, if available. Otherwise, request the item.
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {Store} store
   * @return {Observable<ReduxAction>}
   */
  onInitItem(action$, store) {
    return action$.ofType(this.actions.initItem.TYPE)
      .switchMap(action =>
        this.getCacheState(action)
          .catch(err => {
            this.logger.error({err}, `Failed to retrieve cached state. err=${err && err.message}`);
            return Observable.of(null);
          })
          .map(cachedState => ({
            action,
            cachedState,
          }))
          .takeUntil(this.onDestroyItem(action$, action))
      )
      .mergeMap(({action, cachedState}) => {
        const rootState = store.getState();

        if (!cachedState && !action.payload.existingItem) {
          return this.requestItem(action.payload.id, rootState);

        } else if (action.payload.existingItem) {
          return this.requestSync(rootState, this._options.getState(rootState));

        } else {
          const meta = {
            id: action.payload.id,
          };

          return Observable
            .merge(
              Observable.of(this.actions.hydrateState(cachedState, meta)),
              this.requestSync(rootState, cachedState),
            );

        }
      })
      .catch(epicErrorHandler(this.logger, 'onInitItem'));
  }

  /**
   * Get the cached state for the requested item state.
   * If the init payload contains the item, that supersedes the app cache.
   * @param {ReduxAction<ReduxObservableItemState>} initItemAction
   * @return {Observable<?ReduxObservableItemState>}
   */
  getCacheState(initItemAction) {
    if (initItemAction.payload.existingItem) {
      return Observable.of(null);

    } else if (this._options.cacheOptions) {
      const cacheOptions = this._options.cacheOptions;
      return this.getCachedItem(cacheOptions.getKey(initItemAction.payload.id));

    } else {
      return Observable.of(null);
    }
  }

  getCachedItem(key) {
    const cacheOptions = this._options.cacheOptions;
    return Observable.fromPromise(cacheOptions.localForage.getItem(key).then(state => {
      if (state) {
        return JSON.parse(state, cacheOptions.reviver);
      } else {
        return null;
      }
    }));
  }

  setCachedItem(key, state) {
    const cacheOptions = this._options.cacheOptions;
    return Observable.fromPromise(cacheOptions.localForage.setItem(key, JSON.stringify(state, cacheOptions.replacer)));
  }

  /**
   * When the item is updated, update the cache.
   * CODE_TAG[Store-aware-epic]
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {Store} store
   * @return {Observable<ReduxAction>}
   */
  onItemUpdate(action$, store) {
    return action$.ofType(
      this.actions.loadItemSuccess.TYPE,
      this.actions.syncItemSuccess.TYPE,
    )
      .throttleTime(this._options.cacheOptions.throttle, void 0, {leading: true, trailing: true})
      .mergeMap(() => {
        const cacheOptions = this._options.cacheOptions;
        const state = this._options.getState(store.getState());
        if (!state.init
          || (!state.id && !state.requestId)
          || state.mode === ViewMode.LOADING) {
          return Observable.empty();
        }

        const key = cacheOptions.getKey(state.id);
        return this.setCachedItem(key, state)
          .catch(err => {
            this.logger.error({err}, `Failed to write state to cache with error=${err && err.message}`);
            return Observable.empty();
          });
      })
      .filter(() => false)
      .catch(epicErrorHandler(this.logger, 'onItemUpdate'));
  }

  /**
   * Sets up change-detection for automatic refreshing of data.
   * CODE_TAG[Store-aware-epic]
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {Store} store
   * @param {Object} dependencies Optional dependencies injected into epics
   * @return {Observable<ReduxAction>}
   */
  onSyncItem(action$, store, dependencies) {
    return action$.ofType(this.actions.initItem.TYPE)
      .switchMap(action =>
        // wait for the first successful item load before watching for updates
        // unlike the list observable, the init epic will handle syncing right away if the data is cached
        action$.ofType(this.actions.loadItemSuccess.TYPE)
          .take(1)
          .mergeMap(() =>
            // The item may have changed, trigger a sync
            this._options.syncNotifier(action.payload.id, store, dependencies)
              .throttleTime(this._options.syncNotifierThrottle, void 0, {leading: true, trailing: true})
              .switchMap(() => {
                const rootState = store.getState();
                return this.requestSync(rootState, this._options.getState(rootState));
              })
          )
          .takeUntil(this.onDestroyItem(action$, action))
      )
      .catch(epicErrorHandler(this.logger, 'onSyncItem'));
  }

  /**
   * @param rootState
   * @param {ReduxObservableItemState} state
   * @returns {*}
   */
  requestSync(rootState, state) {
    const etag = getEtag(this._options, state);
    const meta = {
      id: state.id,
    };

    if (!etag) {
      return this.requestItem(state.id, rootState);

    } else {
      return this.requestSyncItem(rootState, state, etag)
        .mergeMap(rawResponse => this.handleSyncItemResult(this._options.parseItemSyncResult(rawResponse), meta))
        // TODO: finalize action
        .catch(err => {
          const mode = viewModeFromErrorStatus(err.status);

          if (mode === ViewMode.ENONET) {
            return Observable.of(this.actions.loadItemError({err, mode}, meta));
          } else {
            // TODO: we may need to allow for not auto-destroying in cases of resources that may get optionally created at request time like chat channels
            return Observable.from([
              this.actions.loadItemError({mode, err}, action.meta),
              this.actions.destroyItem({}, action.meta),
            ]);
          }
        });

    }
  }

  /**
   * @param {Object} rootState
   * @param {ReduxObservableItemState} state
   * @param {String} etag
   * @returns {Observable<T>}
   * @private
   */
  requestSyncItem(rootState, state, etag) {
    if (!state.id || !etag)
      return Observable.empty();

    const syncItemRequest = {
      id: state.id,
      headers: {
        ETag: etag,
      },
    };

    return this._options.syncItemFactory.sendRequest(syncItemRequest, rootState)
      // Test for 304
      .catch(err => get(err, 'status') === 304 ? Observable.of(err) : Observable.throw(err))
      .catch(expoBackoff(
        this.logger,
        'requestSyncItem',
        err => Observable.throw(err),
        (err, errorCount) => errorCount < 5 && isRecoverable(err.status),
      ));
  }

  /**
   * @param {ItemSyncResult} result
   * @param {Object} meta
   * @returns {*}
   */
  handleSyncItemResult(result, meta) {
    if (!result.hasChange) {
      return Observable.empty();

    } else if (result.item) {
      const successPayload = {
        item: result.item,
      };

      return Observable.of(this.actions.syncItemSuccess(successPayload, meta));
    } else {
      return Observable.from([
        this.actions.loadItemError({mode: ViewMode.EOTHER, err: new Error('NO_DATA')}, action.meta),
        this.actions.destroyItem({}, action.meta),
      ]);
    }
  }

  /**
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {Store} store
   * @return {Observable<ReduxAction>}
   */
  onDeleteItem(action$, store) {
    return action$.ofType(this.actions.deleteItem.TYPE)
      .distinctUntilChanged(null, action => action.meta.id)
      .mergeMap(action => this.requestDeleteItem(action.meta.id, action, store.getState()))
      .catch(epicErrorHandler(this.logger, 'onDeleteItem'));
  }

  /**
   * @param {String} id - The ID to delete
   * @param {Object} action
   * @param rootState
   * @return {Observable<ReduxAction>}
   */
  requestDeleteItem(id, action, rootState) {
    return this._options.deleteFactory.sendRequest({id}, rootState)
      .mergeMap(rawResponse => Observable.from([
        this.actions.deleteItemSuccess({}, action.meta),
        this.actions.destroyItem({}, action.meta),
      ]))
      .catch(expoBackoff(
        this.logger,
        'requestDeleteItem',
        err => this.actions.deleteItemError({err, mode: viewModeFromErrorStatus(err.status)}, action.meta),
        (err, errorCount) => errorCount < 5 && isRecoverable(err.status),
      ));
  }

  /**
   * @param {?String} id
   * @param rootState
   * @return {Observable<ReduxAction<LoadItemSuccessPayload|LoadItemError>>}
   */
  requestItem(id, rootState) {
    /** @type {ItemRequest} */
    const request = {
      id,
    };
    const meta = {
      id,
    };

    return this._options.itemFactory.sendRequest(request, rootState)
      .mergeMap(rawResponse => this.handleItemResult(rawResponse, meta))
      .catch(expoBackoff(
        this.logger,
        'requestItem',
        err => this.actions.loadItemError({err, mode: viewModeFromErrorStatus(err.status), meta}),
        (err, errorCount) => errorCount < 5 && isRecoverable(err.status),
      ));
  }

  handleItemResult(rawResponse, meta) {
    const result = this._options.parseItemResult(rawResponse, this._options.parseItem);
    const successPayload = {
      item: result.item,
    };

    return Observable.of(this.actions.loadItemSuccess(successPayload, meta));
  }
}



// WEBPACK FOOTER //
// ./src/js/_libs/redux-observable-list/src/item/epics.js